/**
 * Utility function to handle API response errors consistently
 * Extracts the actual error message from the API response
 */
export const handleApiError = async (response: Response): Promise<string> => {
  try {
    // Try to get the error message from the response body
    const errorData = await response.json();
    
    // Check if this is a license expiration error
    if (errorData && errorData.errorType === 'LICENSE_EXPIRED') {
      return formatLicenseExpirationError(errorData);
    }
    
    // If the API returns a message field, use it
    if (errorData && errorData.message) {
      return errorData.message;
    }
    
    // If no message field, use status text
    return response.statusText || `HTTP ${response.status}`;
  } catch (parseError) {
    // If we can't parse the response, fall back to status text
    return response.statusText || `HTTP ${response.status}`;
  }
};

/**
 * Formats license expiration error messages for better user experience
 */
export const formatLicenseExpirationError = (errorData: any): string => {
  if (!errorData.message) {
    return 'Your company license has expired. Please contact your administrator.';
  }
  
  // If specific modules are mentioned, include them in the message
  if (errorData.expiredModules && errorData.expiredModules.length > 0) {
    const modules = errorData.expiredModules.join(', ');
    return `Your company license has expired for the following modules: ${modules}. Please contact your administrator to renew your subscription.`;
  }
  
  return errorData.message;
};

/**
 * Utility function to create a standardized error message
 */
export const createErrorMessage = (operation: string, error: string): string => {
  return `Failed to ${operation}: ${error}`;
}; 